import { Box, Collapse, Typography } from '@mui/material';
import { useCurrentuser, useSetTimezone, useTimezone } from '@uplift/contexts';
import { useIsMobile } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { Formik } from 'formik';
import React from 'react';
import { Alert } from '../../Alert';
import { CancelButton, SubmitButton } from '../../Form';
import { Select } from '../../Inputs';
import { ModalActions, ModalTitle } from '../../Modal';
import { ModalFullPage, ModalFullPageContent } from '../../ModalFullPage';
import { useUpdateUserTimezone } from '../../UserTimezoneSelect/UserTimezoneSelect.hooks';
import { useAllTimezonesQuery } from '@uplift/queries';


export type EditTimezoneModalProps = {
  open: boolean;
  onClose: () => void;
};


export const EditTimezoneModal: React.FC<EditTimezoneModalProps> = (props) => {
  const isMobile = useIsMobile();
  const me = useCurrentuser();
  const timezone = useTimezone();
  const setTimezone = useSetTimezone();
  const { data: timezones = [], isLoading } = useAllTimezonesQuery();
  const updateTimezone = useUpdateUserTimezone();

  const shortNameMapping = React.useMemo(() => {
    if (!timezones) return {};
    return timezones?.reduce<Record<string, string>>((prev, curr) => ({ ...prev, [curr.ianaName]: curr.shortName }), {});
  }, [ timezones ]);

  const getTimezoneTime = React.useCallback((timezone: string, _destTimezone?: string) => {
    const destTimezone = _destTimezone ?? timezone;
    const datetime = CalendarDateService.date
      .tz('16:00:00', 'HH:MM:SS', timezone)
      .tz(destTimezone);
    const tz = shortNameMapping[destTimezone];

    return `${datetime.format('h a')} ${tz}`;
  }, [ shortNameMapping ]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        timezone: timezone,
      }}
      onSubmit={async (values) => {
        const result = await updateTimezone({ userId: me.id, timezone: values.timezone });

        if (result) {
          setTimezone?.(result);
        }
        props.onClose();
      }}
    >
      {(formik) => (
        <ModalFullPage open={props.open} onClose={props.onClose} mobileOnly transition onExited={() => formik.resetForm()}>
          <ModalTitle onClose={props.onClose}>
            Edit time zone
          </ModalTitle>
          <ModalFullPageContent dividers={isMobile}>
            <Box maxWidth="250px">
              <Select
                disabled={isLoading}
                name="timezone"
                items={timezones?.map(t => ({ value: t.ianaName, label: t.fullName }))}
              />
            </Box>

            <Collapse in={formik.dirty}>
              <Box mt={3}>
                <Alert severity="warning">
                  <Typography gutterBottom>
                    Changing your time zone will convert your sessions’ displayed time.{' '}
                    A session scheduled for {getTimezoneTime(formik.initialValues.timezone)}{' '}
                    will show for you as {getTimezoneTime(formik.initialValues.timezone, formik.values.timezone)}.
                  </Typography>
                  <Typography>
                    <b>Your matching availability and overrides will move with you.</b> If your availability was 9am–5pm {shortNameMapping[formik.initialValues.timezone]}, it will now be 9am-5pm {shortNameMapping[formik.values.timezone]} unless you change it.
                  </Typography>
                </Alert>
              </Box>
            </Collapse>
          </ModalFullPageContent>
          {isMobile ? (
            <ModalActions>
              <SubmitButton disabled={!formik.dirty} color="secondary" fullWidth>Save</SubmitButton>
            </ModalActions>
          ) : (
            <ModalActions>
              <CancelButton onClick={props.onClose}>Close</CancelButton>
              <SubmitButton disabled={!formik.dirty} color="secondary">Save</SubmitButton>
            </ModalActions>
          )}
        </ModalFullPage>
      )}
    </Formik>
  );
};
